import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { PublicLayout } from '../components/PublicLayout';
import { Alert, Button, PasswordField } from '../components/ui';
import { ApiError, api } from '../services/api';
import { clearTokens } from '../services/tokenStore';

export function ResetPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<'form' | 'done' | 'failed'>(token ? 'form' : 'failed');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (password.length < 8) {
      setError(t('auth.weakPassword'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.passwordsDiffer'));
      return;
    }
    setBusy(true);
    try {
      await api.resetPassword(token, password);
      clearTokens(); // every session was revoked server-side
      setState('done');
    } catch (err) {
      if (err instanceof ApiError && err.is('weak_password')) setError(t('auth.weakPassword'));
      else if (err instanceof ApiError && err.is('bad_token')) setState('failed');
      else if (err instanceof ApiError && err.is('rate_limited')) setError(t('app.rateLimited'));
      else setError(t('app.error'));
    } finally {
      setBusy(false);
    }
  };

  if (state === 'done') {
    return (
      <PublicLayout title={t('reset.title')}>
        <Alert kind="success">{t('reset.done')}</Alert>
        <Link to="/login">
          <Button className="w-full">{t('auth.signIn')}</Button>
        </Link>
      </PublicLayout>
    );
  }
  if (state === 'failed') {
    return (
      <PublicLayout title={t('reset.title')}>
        <Alert kind="error">{t('reset.failed')}</Alert>
        <Link to="/forgot">
          <Button className="w-full" variant="secondary">
            {t('reset.again')}
          </Button>
        </Link>
      </PublicLayout>
    );
  }
  return (
    <PublicLayout title={t('reset.title')} subtitle={t('reset.subtitle')}>
      <form onSubmit={submit} className="flex flex-col gap-3" noValidate>
        <PasswordField id="password" label={t('account.newPassword')} autoComplete="new-password" hint={t('auth.passwordRule')} value={password} onChange={(e) => setPassword(e.target.value)} show={show} onToggle={() => setShow((v) => !v)} required />
        <PasswordField id="confirm" label={t('auth.confirmPassword')} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} show={show} onToggle={() => setShow((v) => !v)} required />
        {error && <Alert kind="error">{error}</Alert>}
        <Button type="submit" disabled={busy || !password || !confirm} className="w-full">
          {busy ? t('reset.submitting') : t('reset.submit')}
        </Button>
      </form>
    </PublicLayout>
  );
}
